import React from 'react';
import { Plus, Pencil, Globe } from 'lucide-react';
import { Floor } from '../types';

interface FloorSelectorProps {
  floors: Record<string, Floor>;
  currentFloor: string;
  setCurrentFloor: (floorKey: string) => void;
  setShowFloorDesigner: (show: boolean) => void;
  openDesignerForEdit: (floorId: string) => void;
  mmrBuilt?: boolean;
  onOpenMmr: () => void;
}

const FloorSelector: React.FC<FloorSelectorProps> = ({
  floors,
  currentFloor,
  setCurrentFloor,
  setShowFloorDesigner,
  openDesignerForEdit,
  mmrBuilt = false,
  onOpenMmr,
}) => {
  if (!floors || Object.keys(floors).length === 0) {
    return null;
  }

  return (
    <div className="mb-4 flex border-b border-gray-700 items-end">
      {Object.entries(floors)
        .sort(([a], [b]) => {
          // Keys look like "floor1" or "floor-1719…"; sort by the trailing number, then key
          const na = parseInt(a.replace(/\D/g, ''), 10);
          const nb = parseInt(b.replace(/\D/g, ''), 10);
          if (isNaN(na) || isNaN(nb)) return a.localeCompare(b);
          return na - nb;
        })
        .map(([floorKey, floorData]) => (
          <button
            key={floorKey}
            onClick={() => setCurrentFloor(floorKey)}
            className={`px-4 py-2 -mb-px border-b-2 transition-colors
              ${currentFloor === floorKey
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'}
            `}
          >
            {floorData.name}
          </button>
        ))}

      {/* MMR button — opens popup */}
      <button
        onClick={onOpenMmr}
        className={`px-4 py-2 -mb-px border-b-2 flex items-center gap-1.5 transition-colors border-transparent
          ${mmrBuilt
            ? 'text-cyan-500 hover:text-cyan-300 hover:border-cyan-700'
            : 'text-gray-500 hover:text-gray-300 hover:border-gray-500'}
        `}
        title={mmrBuilt ? 'Meet-Me Room (operational)' : 'Meet-Me Room (not built)'}
      >
        <Globe className="w-3.5 h-3.5" />
        MMR
        {!mmrBuilt && <span className="text-xs text-gray-600 ml-0.5">*</span>}
      </button>

      <div className="ml-auto flex items-center space-x-2 px-2">
        <button
          className="px-3 py-2 text-gray-400 hover:text-gray-200 border border-gray-700 rounded"
          onClick={() => openDesignerForEdit(currentFloor)}
          title="Edit current floor layout"
        >
          <Pencil className="w-4 h-4 inline-block mr-1" /> Edit Layout
        </button>
        <button
          className="px-3 py-2 text-gray-400 hover:text-gray-200 border border-gray-700 rounded"
          onClick={() => setShowFloorDesigner(true)}
          title="Design a new floor"
        >
          <Plus className="w-4 h-4 inline-block mr-1" /> Add Floor
        </button>
      </div>
    </div>
  );
};

export default FloorSelector;
